/**
 * Banner — feedback message strip.
 *
 * Consolidates `ram-wizard__banner`, `rtm-banner`, per-step inline banners in
 * RAO, and the info-tint placeholder used by RCC/RDL ComingSoonPanel. One
 * surface per tone (info/warn/err/ok). Tokens only — no literal hex.
 */
import type { ReactNode } from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle, Info, type LucideIcon } from '@retail-ops/panel-shell/fi';

export type BannerTone = 'info' | 'warn' | 'err' | 'ok';

export interface BannerProps {
  tone: BannerTone;
  icon?: ReactNode;
  title?: string;
  children?: ReactNode;
  className?: string;
}

interface ToneSpec {
  bg: string;
  text: string;
  border: string;
  Icon: LucideIcon;
}

const TONES: Record<BannerTone, ToneSpec> = {
  info: {
    bg: 'var(--info-bg)',
    text: 'var(--info-text)',
    border: 'var(--pcb-blue)',
    Icon: Info,
  },
  warn: {
    bg: 'var(--warn-bg)',
    text: 'var(--warn-text)',
    border: 'var(--pcb-amber-text)',
    Icon: AlertTriangle,
  },
  err: {
    bg: 'var(--err-bg)',
    text: 'var(--err-text)',
    border: 'var(--pcb-red)',
    Icon: AlertOctagon,
  },
  ok: {
    bg: 'var(--ok-bg)',
    text: 'var(--ok-text)',
    border: 'var(--pcb-green)',
    Icon: CheckCircle,
  },
};

/** Primitive: Banner. */
export function Banner({
  tone,
  icon,
  title,
  children,
  className,
}: BannerProps): JSX.Element {
  const spec = TONES[tone];
  const IconComp = spec.Icon;
  const role = tone === 'err' ? 'alert' : 'status';

  return (
    <div
      role={role}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-5)',
        padding: 'var(--sp-5) var(--sp-7)',
        background: spec.bg,
        color: spec.text,
        border: `1px solid ${spec.border}`,
        borderRadius: 'var(--r-md)',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}>
        {icon ?? <IconComp size={16} aria-hidden />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{ fontWeight: 700, marginBottom: children ? 2 : 0 }}>{title}</div>
        )}
        {children}
      </div>
    </div>
  );
}
